import { get as getVal } from 'dot-prop';
import React, { ReactElement } from 'react';
import Block from './block';
import FixedBlock from './fixed-block';
import ErrorBoundary from '../../common/error-boundary';
import { lazyBlockHoc } from './lazy-block';
import { BlockModuleRenderConfig, JsModuleRenderConfig } from './interfaces';

interface FreeDomJsModuleDeclaration {
  renderType: 'js_ssr' | 'csr' | 'dx_ssr' | 'dx' | 'native' | 'tangram';
  renderConfig: JsModuleRenderConfig;
  globalDataKeys?: Array<string>;
  /**
   * module为js模块导出名
   *
   * @type {string}
   * @memberof FreeDomJsModuleDeclaration
   */
  moduleName: string;
  nodeType: string;
}

interface FreeDomBlockModuleDeclaration {
  renderType: 'js_ssr';
  renderConfig: BlockModuleRenderConfig;
  /**
   * Block类型的module，默认为空div容器
   *
   * @type {'Block'}
   * @memberof FreeDomBlockModuleDeclaration
   */
  nodeType: 'Block';
}

interface FreeDomModule {
  jsModule: Function;
  exportName: string;
}

interface FreeDomModuleResolver {
  (
    moduleDeclaration:
      | FreeDomJsModuleDeclaration
      | FreeDomBlockModuleDeclaration,
  ): FreeDomModule;
}

interface FreeDomModuleAsyncResolver {
  (
    moduleDeclaration:
      | FreeDomJsModuleDeclaration
      | FreeDomBlockModuleDeclaration,
  ): Promise<FreeDomModule>;
}

interface FreeDomStructureNode {
  [moduleKey: string]: Array<string>;
}

interface FreeDomGlobalData {
  [key: string]: any;
}

interface FreeDomModuleManager {
  getModuleByModuleName: (moduleName: string) => FreeDomModule;
  getModuleByStructureName: (structureName: string) => FreeDomModule;
  getModuleDataByModuleName: (moduleName: string) => FreeDomGlobalData;
}

interface FreeDomHierarchy {
  root: string;
  structure: FreeDomStructureNode;
}

interface NodeMap {
  [moduleKey: string]:
    | FreeDomJsModuleDeclaration
    | FreeDomBlockModuleDeclaration;
}
interface FreeDom {
  globalData: FreeDomGlobalData;
  hierarchy: FreeDomHierarchy;
  nodeMap: NodeMap;
}

interface GetTreeSync {
  (freeDomConfig: FreeDom, moduleLib: ModuleLib): ReactElement;
}

interface RenderAsync {
  (freeDomConfig: FreeDom): ReactElement;
}

interface StructureTree {
  structureName: string;
  moduleData: FreeDomBlockModuleDeclaration | FreeDomJsModuleDeclaration;
  children: StructureTree[];
}

interface BuildTree {
  (
    structureName: string,
    structureFlatTree: FreeDomStructureNode,
    nodeMap: NodeMap,
    tree: any,
  ): StructureTree;
}

interface BuildComponentTree {
  (
    structureName: string,
    tree: StructureTree,
    globalData: FreeDomGlobalData,
    moduleLib: ModuleLib,
  ): ReactElement;
}

interface RenameRootNode {
  (hierarchy: FreeDomHierarchy): FreeDomStructureNode;
}

interface ModuleLib {
  [componentName: string]: Function;
}

export const buildTree: BuildTree = function(
  structureName,
  structureFlatTree,
  nodeMap,
  tree = {},
): StructureTree {
  tree.structureName = structureName;
  tree.children = [];
  tree.moduleData = nodeMap[structureName] || {};
  const curNodeChildren = structureFlatTree[structureName];
  if (curNodeChildren && Array.isArray(curNodeChildren)) {
    curNodeChildren.forEach(childName => {
      if (nodeMap[childName]) {
        tree.children.push(
          buildTree(childName, structureFlatTree, nodeMap, {}),
        );
      }
    });
  }
  return tree;
};

const LazyBlock = lazyBlockHoc(Block);

interface CreateElement {
  (
    structureName: string,
    moduleData: StructureTree['moduleData'],
    globalData: FreeDomGlobalData,
    children: Array<ReactElement>,
    moduleLib: ModuleLib,
  ): ReactElement;
}
interface SafeRenderFunction extends Function {
  __safeRender?: boolean;
}
interface SafeRender {
  (
    classFunc: SafeRenderFunction | null | undefined,
    structureName: string,
  ): SafeRenderFunction;
}

export const jsModuleSafeRender: SafeRender = function(
  WrappedComponent,
  structureName,
) {
  return props => {
    return (
      <ErrorBoundary>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

export const createElement: CreateElement = function(
  structureName: string,
  moduleData: StructureTree['moduleData'],
  globalData: FreeDomGlobalData,
  children: Array<ReactElement>,
  moduleLib: ModuleLib,
): ReactElement {
  const isRootElement = structureName === 'root';
  const isBlockElement = moduleData.nodeType === 'Block';
  const isJsModuleElement = moduleData.nodeType !== 'Block';

  const { renderConfig = {} } = moduleData;

  if (isRootElement || isBlockElement) {
    const {
      style,
      className,
      dataProps,
      lazy,
      fixedWhenOutview,
    } = renderConfig;
    const props = {
      id: structureName,
      key: structureName,
      style,
      className,
      dataProps,
      children,
      fixedWhenOutview,
    };

    if (lazy) return <LazyBlock {...props} />;

    if (fixedWhenOutview) {
      return <FixedBlock {...props} />;
    } else {
      return <Block {...props} />;
    }
  }

  const moduleLibHasThisModule = (
    moduleData: StructureTree['moduleData'],
  ): boolean =>
    !!(
      (moduleData as FreeDomJsModuleDeclaration) &&
      (moduleData as FreeDomJsModuleDeclaration).moduleName &&
      moduleLib[(moduleData as FreeDomJsModuleDeclaration).moduleName]
    );

  if (isJsModuleElement && moduleLibHasThisModule(moduleData)) {
    const LibComponent =
      moduleLib[(moduleData as FreeDomJsModuleDeclaration).moduleName];
    const SafeLibComponent = jsModuleSafeRender(LibComponent, structureName);
    const propKeys = (moduleData as FreeDomJsModuleDeclaration).globalDataKeys;
    interface Props {
      [key: string]: any;
    }
    const props: Props = {};
    const getEndStringInDotKey = function(key: string): string {
      if (key.indexOf('.') > -1) {
        return key.split('.').pop() || '';
      }
      return key;
    };
    propKeys?.forEach(key => {
      props[getEndStringInDotKey(key)] = getVal(globalData, key);
    });
    return (
      <Block
        id={structureName}
        key={structureName}
        style={renderConfig.style}
        className={renderConfig.className}
        dataProps={renderConfig.dataProps}
      >
        <SafeLibComponent
          {...props}
          {...(renderConfig as JsModuleRenderConfig).customProps}
        />
        {children}
      </Block>
    );
  }

  return (
    <div key={structureName}>no module found by name: {structureName}</div>
  );
};

export const buildComponentTree: BuildComponentTree = function(
  structureName,
  tree,
  globalData,
  moduleLib,
): ReactElement {
  let c: Array<ReactElement> = [];
  tree.children.forEach(oneStructureTree => {
    c.push(
      buildComponentTree(
        oneStructureTree.structureName,
        oneStructureTree,
        globalData,
        moduleLib,
      ),
    );
  });
  return createElement(
    structureName,
    tree.moduleData,
    globalData,
    c,
    moduleLib,
  );
};

export const renameRootNode: RenameRootNode = function(hierarchy) {
  const rootNodeName = hierarchy.root;
  const rootNode = hierarchy.structure[rootNodeName];
  const structure = hierarchy.structure;

  structure.root = rootNode;
  delete structure[rootNodeName];

  return structure;
};

export const getTreeSync: GetTreeSync = function(
  freeDomConfig: FreeDom,
  moduleLib: ModuleLib,
): ReactElement {
  const tree = buildTree(
    'root',
    renameRootNode(freeDomConfig.hierarchy),
    freeDomConfig.nodeMap,
    {},
  );
  const componentTree = buildComponentTree(
    'root',
    tree,
    freeDomConfig.globalData,
    moduleLib,
  );
  return <div>{componentTree}</div>;
};

export const renderAsync: RenderAsync = function(freeDomConfig: FreeDom) {
  const tree = buildTree(
    'root',
    renameRootNode(freeDomConfig.hierarchy),
    freeDomConfig.nodeMap,
    {},
  );
  const componentTree = buildComponentTree(
    'root',
    tree,
    freeDomConfig.globalData,
    {},
  );
  return <div>{componentTree}</div>;
};
